import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { ZendeskConfig } from '@app/core/services/zendesk-config';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { NgxZendeskWebwidgetService } from 'ngx-zendesk-webwidget';
import { BehaviorSubject, combineLatest } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AuthService, ProviderService, UiStateService } from './core/services';
import { MerakiAuthService } from './core/services/meraki-auth.service';
import { BrowserTypes } from './core/services/state/ui-state/ui-state.store';
import { PrivacyPolicyDialogComponent } from './shared/components/privacy-policy-dialog/privacy-policy-dialog.component';
import { isHbcGoUrl } from '@app/core/functions/hbc-go-url';

declare global {
  interface Window {
    productFruitsUser: any;
    productFruits: any;
    $productFruits: any;
    $crisp: any;
    CRISP_WEBSITE_ID: any;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  BrowserTypes = BrowserTypes;

  browserType$ = this.uiStateService.browserType$.pipe(distinctUntilChanged(), untilDestroyed(this), shareReplay(1));

  sidebarFlyout$ = this.uiStateService.sidebarFlyout$;

  privacyPolicyDateAccepted$ = this.providerService.PrivacyPolicyDateAccepted$;

  loginToFirebase$ = new BehaviorSubject(true);

  noraRecorderVisible$ = this.uiStateService.noraRecorderVisible$;

  // Track if we're in HBC Go route (should not use main app layout)
  // Initialize with current URL to avoid race condition on initial page load
  isHbcGoRoute$: BehaviorSubject<boolean>;

  constructor(
    private uiStateService: UiStateService,
    private router: Router,
    private route: ActivatedRoute,
    private ngxZendeskWebwidgetService: NgxZendeskWebwidgetService,
    private authService: AuthService,
    private providerService: ProviderService,
    private dialog: MatDialog,
    private nexusApi: MerakiAuthService,
    private snackbar: MatSnackBar
  ) {
    // Initialize with current URL at construction time
    this.isHbcGoRoute$ = new BehaviorSubject<boolean>(isHbcGoUrl(this.router.url));
  }

  ngOnInit() {
    // Detect HBC Go routes to exclude from main layout
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map((event: NavigationEnd) => isHbcGoUrl(event.url)),
        distinctUntilChanged(),
        tap(isHbcGo => {
          this.isHbcGoRoute$.next(isHbcGo);
        }),
        untilDestroyed(this)
      )
      .subscribe();

    // monitor children
    this.router.events
      .pipe(
        filter(routeEvents => routeEvents instanceof NavigationEnd),
        map(() =>
          this.route.firstChild && this.route.firstChild.firstChild
            ? {
                tenantId: this.route.firstChild.firstChild.snapshot.params.tenantId,
                patientId: this.route.firstChild.firstChild.snapshot.params.patientId,
              }
            : { tenantId: this.route.firstChild.snapshot.params.tenantId, patientId: null }
        ),
        distinctUntilChanged(),
        untilDestroyed(this)
      )
      .subscribe({
        next: params => {
          this.uiStateService.changeCurrentPatientId(params.patientId);
        },
      });

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationStart),
        map(e => (e as NavigationStart).url),
        map(url => {
          const match = url.match(/\/patient\/([^\/]+)/);
          return match ? match[1] : null;
        }),
        filter((patientId): patientId is string => !!patientId),
        tap(patientId => {
          // Clear stale loading tabs from any previously cancelled navigations
          this.uiStateService.clearLoadingPatientTabs();
          // Only add skeleton if patient is not already loaded (avoids flicker on tab re-click)
          const alreadyLoaded = this.uiStateService.getCurrentPatientTabs().some(t => t.patientId === patientId && !t.loading);
          if (!alreadyLoaded) {
            this.uiStateService.addActivePatient({ patientId, patientName: '', loading: true });
          }
        }),
        tap(patientId => this.uiStateService.changeCurrentPatientId(patientId)),
        untilDestroyed(this)
      )
      .subscribe();

    // this.initZendesk();

    this.loginToFirebase$
      .pipe(
        switchMap(() => this.nexusApi.isLoggedIn()),
        filter(loggedIn => this.authService.token && !loggedIn),
        exhaustMap(() => this.nexusApi.signIntoFirebase(this.authService.token)),
        untilDestroyed(this)
      )
      .subscribe();

    this.initProductFruits();

    this.showPrivacyPolicyDialog();

    this.uiStateService.isPortraitMode$
      .pipe(
        distinctUntilChanged(),
        untilDestroyed(this),
        tap(
          portraitMode =>
            portraitMode && this.snackbar.open('Please use landscape mode for better user experience', 'OK', { duration: 5000 })
        )
      )
      .subscribe();
  }

  initProductFruits() {
    combineLatest([
      this.providerService.provider$,
      this.authService.auth$,
      this.providerService.patientSummaryEnabled$,
      this.providerService.IsDisabledNoraRecording$,
      this.providerService.IsDisabledClinicalAssistant$,
    ])
      .pipe(
        // Wait for all data to be available
        filter(([provider, user]) => !!user && !!provider && !!user.username),
        // Allow time for all observables to emit their complete state
        debounceTime(300),
        // Only take the first complete emission to initialize once
        take(1),
        // Contract flags are static supporting data — withLatestFrom keeps the primary
        // 5-tuple intact (combineLatest's typed overloads cap at 6) and aligns with
        // docs/rxjs-patterns.md ("primary trigger + supporting data").
        withLatestFrom(this.providerService.isHbcGo$, this.providerService.isFull$),
        tap(([[provider, user, patientHistorySummaryEnabled, noraDisabled, clinicalAssistantDisabled], isHbcGo, isFull]) => {
          // Match the HbcGoRedirectGuard rule: a "Go-only" user has the HBC Go contract
          // and no Full HBC contract (Full users land on main HBC even if Go is enabled).
          const isHbcGoUser = !!isHbcGo && !isFull;
          const productTier = isHbcGoUser ? 'HBC Go' : isFull ? 'HBC Full' : 'HBC Lite';

          // set up product fruits user data
          window.productFruitsUser = {
            // username: user.uid,
            username: user.username || '',
            email: provider.EmailAddress,
            firstname: user.fullName || '', // just have full name
            lastname: '',
            // signUpAt: '',//user['email'] ? user['email'] : '',// todo
            role: this.authService.superAdmin ? 'admin' : '', // todo confirm why we care only about admin role
            // role: auth['admin'] ? 'admin' : '',

            props: {
              'Billing Practice Number': provider.PracticeNumber || '',
              'Treating Provider Number': provider.TreatingPracticeNumber || '',
              Speciality: provider.SpecialityDescription || '',
              'Patient History Summary': patientHistorySummaryEnabled || false,
              Nora: !noraDisabled,
              'Clinical Assistant': !clinicalAssistantDisabled,
              // Lets Product Fruits target tours/onboarding at HBC Go vs Full users.
              'HBC Go': isHbcGoUser,
              'Product Tier': productTier,
            },
          };

          // set up product fruits script, initialise and add it to the head tag
          ((w, d, u) => {
            w.$productFruits = w.$productFruits || [];
            w.productFruits = w.productFruits || {};
            w.productFruits.scrV = '2';
            const a = d.getElementsByTagName('head')[0];
            const r: any = d.createElement('script');
            r.async = 1;
            r.src = u;
            a.appendChild(r);
            w.$productFruits.push(['init', '5zCQ82ryDNVoMqbJ', 'en', w.productFruitsUser]); // hbc key, nothing shown
          })(window, document, 'https://app.productfruits.com/static/script.js');

          // set up crisp script and add it to the head tag
          ((w, d, s) => {
            w.$crisp = w.$crisp || [];
            w.CRISP_WEBSITE_ID = 'bd9eaa37-a026-4808-9b95-a63b6b45a11c';

            const a = d.getElementsByTagName('head')[0];
            const r: any = d.createElement('script');
            r.async = 1;
            r.src = s;
            a.appendChild(r);
            w.$crisp.push(['do', 'chat:hide']);
            // set crisp user data
            w.$crisp.push([
              'set',
              'session:data',
              [
                [
                  ['FullName', user.fullName],
                  ['bpn', provider.PracticeNumber],
                  ['email', ''],
                ],
              ],
            ]);
          })(window, document, 'https://client.crisp.chat/l.js');
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  initZendesk() {
    this.ngxZendeskWebwidgetService?.initZendesk(new ZendeskConfig());
  }

  showHelp() {
    this.ngxZendeskWebwidgetService?.zE.activate();
  }

  onRouteActivate(e) {
    // TODO: Remove once florence user migration is done.
    this.loginToFirebase$.next(true);
  }

  showPrivacyPolicyDialog() {
    this.providerService.providerOptions$
      .pipe(
        filter(options => Object.keys(options).length > 0), // Wait for actual data, skip startWith({})
        take(1),
        map(options => options?.PrivacyPolicyDateAccepted || ''),
        filter(val => val !== this.providerService.PrivacyPolicyLatestDate && this.authService.authenticated),
        withLatestFrom(this.providerService.provider$, this.providerService.isHbcGo$, this.providerService.isFull$),
        // HBC Go users already see a privacy-policy link on the login page, so the in-app
        // prompt is suppressed for them. Mirror the "Go-only" rule used for Product Fruits:
        // a Full contract takes precedence (those users land on main HBC and still get prompted).
        filter(([, , isHbcGo, isFull]) => !(isHbcGo && !isFull)),
        tap(([val, provider]) => {
          this.dialog.open(PrivacyPolicyDialogComponent, {
            height: '600px',
            width: '500px',
            disableClose: true,
            data: {
              tenantId: provider.PracticeId,
            },
          });
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {}
}
